import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Tag as TagIcon, X } from 'lucide-react';
import { getBlogPosts, getBlogCategories, getBlogTags } from '@/lib/api';

const formatDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const readingTime = (html) => {
  if (!html) return '1 min read';
  const text = html.replace(/<[^>]+>/g, ' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const mins = Math.max(1, Math.round(words / 220));
  return `${mins} min read`;
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const activeCategory = searchParams.get('category') || '';
  const activeTag = searchParams.get('tag') || '';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (activeCategory) params.category = activeCategory;
        if (activeTag) params.tag = activeTag;
        const [postsRes, catsRes, tagsRes] = await Promise.all([
          getBlogPosts(params),
          getBlogCategories(),
          getBlogTags(),
        ]);
        if (cancelled) return;
        setPosts(postsRes.data.posts || []);
        setCategories(catsRes.data || []);
        setTags(tagsRes.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeCategory, activeTag]);

  useEffect(() => {
    document.title = 'Blog — Lalita Properties';
  }, []);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const clearFilters = () => setSearchParams({});

  const hero = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="pt-24 min-h-screen bg-white" data-testid="blog-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-charcoal mb-3">
            Lalita Journal
          </h1>
          <p className="font-body text-charcoal-light text-lg max-w-2xl">
            Insights on Bali villa investment, property management, lifestyle and the local market — straight from our team.
          </p>
        </motion.div>

        {/* Filters */}
        {(categories.length > 0 || tags.length > 0) && (
          <div className="mb-10 space-y-4">
            {categories.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-charcoal-light mr-2">Categories:</span>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => updateFilter('category', cat === activeCategory ? '' : cat)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      activeCategory === cat
                        ? 'bg-ubud-jungle text-white'
                        : 'bg-warm-sand text-charcoal hover:bg-warm-sand-dark'
                    }`}
                    data-testid={`blog-cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
            {tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-charcoal-light mr-2">Tags:</span>
                {tags.slice(0, 12).map((t) => (
                  <button
                    key={t}
                    onClick={() => updateFilter('tag', t === activeTag ? '' : t)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors inline-flex items-center gap-1 ${
                      activeTag === t
                        ? 'bg-charcoal text-white'
                        : 'bg-white border border-gray-200 text-charcoal-light hover:border-charcoal'
                    }`}
                  >
                    <TagIcon className="w-3 h-3" />{t}
                  </button>
                ))}
              </div>
            )}
            {(activeCategory || activeTag) && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-1 text-sm text-charcoal-light hover:text-charcoal"
              >
                <X className="w-4 h-4" /> Clear filters
              </button>
            )}
          </div>
        )}

        {/* Posts */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-warm-sand rounded-2xl h-80 animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-gray-200 rounded-2xl">
            <p className="font-body text-charcoal-light">No posts yet. Check back soon.</p>
          </div>
        ) : (
          <>
            {/* Hero post */}
            {hero && (
              <Link
                to={`/blog/${hero.slug}`}
                className="group block mb-12"
                data-testid={`blog-hero-post`}
              >
                <div className="grid md:grid-cols-2 gap-8 items-center bg-warm-sand rounded-3xl overflow-hidden">
                  <div className="aspect-[4/3] md:aspect-auto md:h-full overflow-hidden">
                    {hero.cover_image ? (
                      <img
                        src={hero.cover_image}
                        alt={hero.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-ubud-jungle/10 flex items-center justify-center">
                        <span className="font-heading text-3xl text-ubud-jungle/40">Lalita</span>
                      </div>
                    )}
                  </div>
                  <div className="p-8 md:p-12">
                    {hero.category && (
                      <span className="inline-block text-xs font-medium tracking-wide uppercase text-ubud-jungle mb-3">
                        {hero.category}
                      </span>
                    )}
                    <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-charcoal mb-4 group-hover:text-ubud-jungle transition-colors">
                      {hero.title}
                    </h2>
                    {hero.excerpt && (
                      <p className="font-body text-charcoal-light leading-relaxed mb-6">
                        {hero.excerpt}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-charcoal-muted">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(hero.published_at || hero.created_at)}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{readingTime(hero.content)}</span>
                      <span>by {hero.author || 'Lalita Team'}</span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Grid */}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {rest.map((post, idx) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05 }}
                    data-testid={`blog-post-${post.slug}`}
                  >
                    <Link to={`/blog/${post.slug}`} className="group block">
                      <div className="aspect-[4/3] overflow-hidden rounded-2xl mb-4 bg-warm-sand">
                        {post.cover_image ? (
                          <img
                            src={post.cover_image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-ubud-jungle/10 flex items-center justify-center">
                            <span className="font-heading text-2xl text-ubud-jungle/40">Lalita</span>
                          </div>
                        )}
                      </div>
                      {post.category && (
                        <span className="text-xs font-medium tracking-wide uppercase text-ubud-jungle">
                          {post.category}
                        </span>
                      )}
                      <h3 className="font-heading text-xl font-semibold text-charcoal mt-2 mb-2 group-hover:text-ubud-jungle transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="font-body text-sm text-charcoal-light line-clamp-3 mb-3">
                          {post.excerpt}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-charcoal-muted">
                        <span>{formatDate(post.published_at || post.created_at)}</span>
                        <span>·</span>
                        <span>{readingTime(post.content)}</span>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
